import { useState, useEffect } from 'react'

import { Loader, Modal } from '@/components/ui'
import { useAssetState } from '@/hooks'

import AssetDetailContent, { type AssetDetailTab } from './AssetDetailContent'
import AssetDetailHeader from './AssetDetailHeader'

type AssetDetailModalProps = {
  show: boolean
  onHide: () => void
  assetId?: number
  companyId?: number
}

export default function AssetDetailModal({ show, onHide, assetId, companyId }: AssetDetailModalProps) {
  const [activeTab, setActiveTab] = useState<AssetDetailTab>('overview')
  // The modal body stays mounted across close (persistent <dialog>), so bump this
  // key whenever the modal closes to remount the content on reopen — that resets any
  // selected chart range or navigated date back to today's default.
  const [resetKey, setResetKey] = useState(0)
  useEffect(() => {
    if (!show) setResetKey((k) => k + 1)
  }, [show])
  const { data: asset } = useAssetState(show ? assetId : undefined)

  if (!assetId) {
    return null
  }

  if (!asset) {
    return (
      <Modal
        title="Loading..."
        show={show}
        onHide={onHide}
        maxWidth="1280px"
        bodyContent={
          <div className="max-h-[70vh] min-h-[40vh] overflow-auto flex flex-col justify-center">
            <Loader />
          </div>
        }
        hideFooter={true}
      />
    )
  }

  return (
    <Modal
      title={<AssetDetailHeader asset={asset} />}
      show={show}
      onHide={onHide}
      maxWidth="1440px"
      bodyContent={
        // Unmounted while the (persistent) dialog is closed, so the route history's live
        // polling stops with it.
        show && (
          <AssetDetailContent
            key={resetKey}
            asset={asset}
            companyId={companyId}
            activeTab={activeTab}
            setActiveTab={setActiveTab}
          />
        )
      }
      hideFooter={true}
    />
  )
}
